import { Box, Heading } from '@zillow/constellation-10';
import { FolderSelector } from '@zg-rentals/satellite-components';
import { useAuth } from '@zg-rentals/auth-comet';
import React, { useContext } from 'react';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { emitStarredConversationsFilterEvent } from '../../constants/clickstreamEvents/starConversations';
import { emitOpenFilterMenuEvent, OPEN_FILTER_MENU_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/filterMenu';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import FiltersSelector from '../FiltersSelector';
import RentalJourneyFilters from '../RentalJourneyFilters';
import {
  foldersSelectorContainerStyles,
  headerContainerStyles,
  headingStyles,
  mobileWebFiltersContainer,
  rentalJourneyFiltersContainerStyles,
} from './styles';
import { Folder, ReadRepliedStatus, RenterProgressStatus } from '../../types/GraphqlTypes';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import useDevices from '../../hooks/useDevices';

/**
 * This component contains all the content above the messaging portion of Inbox Page
 * such as the read filters, folders, and Rental Journey filters
 */
export default function InboxHeader() {
  const { folder: currentFolder, mobileView } = useContext(InboxPageContext);
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { user } = useAuth();
  const { isMobileView } = useDevices();

  const shouldHideHeaderOnMobile = mobileView !== InboxPageMobileView.ConversationList && isMobileView;

  const handleFolderChange = (selectedOption: Folder) => {
    if (selectedOption === Folder.Favorite) {
      emitStarredConversationsFilterEvent(user);
    }

    if (selectedOption !== currentFolder) {
      changeFiltersOrFolders({
        folder: selectedOption,
        // Reset all other filters
        renterProgressStatus: RenterProgressStatus.ShowAll,
        readRepliedStatus: ReadRepliedStatus.ShowAll,
        byProperty: false,
      });
    }
  };

  const handleMenuOpen = () => {
    emitOpenFilterMenuEvent(user);
  };

  return (
    <Box
      css={{
        ...headerContainerStyles,
        display: shouldHideHeaderOnMobile ? 'none' : 'flex',
      }}
    >
      <Heading level={1} fontFamily="heading-alt" css={headingStyles}>
        Messages
      </Heading>

      {/* Desktop: Tabs */}
      <Box css={rentalJourneyFiltersContainerStyles}>
        <RentalJourneyFilters />
      </Box>

      {/* Desktop: Filters button */}
      <Box
        css={{
          display: { base: 'none', xxl: 'block' },
        }}
      >
        <FiltersSelector />
      </Box>

      {/* Inbox button - always last on row 1 */}
      <Box css={foldersSelectorContainerStyles} data-eventid={OPEN_FILTER_MENU_EVENT_TYPE_ID}>
        <FolderSelector
          actions={{ onFolderChange: handleFolderChange, onMenuOpen: handleMenuOpen }}
          selectedFolder={currentFolder}
        />
      </Box>

      {/* Mobile only: Filters + Tabs on row 2 */}
      <Box css={mobileWebFiltersContainer}>
        <Box css={{ flexShrink: 0 }}>
          <FiltersSelector />
        </Box>
        <Box css={{ flex: 1, minWidth: 0, overflowX: 'auto', height: '48px' }}>
          <RentalJourneyFilters />
        </Box>
      </Box>
    </Box>
  );
}
